//
//  ChangePassword.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: October 14, 2026
//  Author: Bob Keathley
//
//  Change-password form (SIG-66 sec 9). Forced mode is the only screen for a
//  temporary-password login and has no cancel; otherwise it is self-service.
//  The server revokes other sessions; this one stays signed in.
//
import { useState, type FormEvent } from 'react'
import { api } from '../api/client'
import { isOk } from '../api/envelope'
import type { PasswordChange } from '../api/types'
import { auth, type Session } from '../auth'
import BrandMark from './BrandMark'
import PasswordInput from './PasswordInput'

const MIN = 12

/**
 * SIG-66 §9. `forced`: the account is on a temp password, so this is the only screen (full-page,
 * no cancel). Otherwise it is the self-service "Change password" form opened from the user menu.
 * Any role: `POST /v1/auth/password {current_password, new_password}`; other sessions are revoked
 * server-side and this one stays signed in.
 */
export default function ChangePassword({ session, forced = false, onDone, onCancel }: { session: Session; forced?: boolean; onDone?: () => void; onCancel?: () => void }) {
  const [next, setNext] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    // Read the fields from the form itself: a browser autofill fills the box without React seeing it.
    const f = new FormData(e.currentTarget)
    const current = String(f.get('current') ?? '')
    const newPw = String(f.get('new') ?? '')
    const confirm = String(f.get('confirm') ?? '')
    if (!current) { setError('Enter your current (temporary) password.'); return }
    if (newPw.length < MIN) { setError(`Your new password needs at least ${MIN} characters.`); return }
    if (newPw !== confirm) { setError("The two new passwords don't match."); return }
    setBusy(true); setError(null)
    const body: PasswordChange = { current_password: current, new_password: newPw }
    const r = await api<unknown>('/auth/password', { method: 'POST', body: JSON.stringify(body) }, session.token)
    setBusy(false)
    if (!isOk(r)) {
      setError(r.error.code === 'invalid_current_password' ? 'Your current password is incorrect.' : r.error.message)
      return
    }
    setNext('')
    auth.passwordChanged()
    onDone?.()
  }

  const form = (
    <form className="card" onSubmit={submit} noValidate aria-label="Choose a new password">
      <BrandMark variant="mark" size={88} />
      <p className="brand"><b>Signet</b> {forced ? 'Choose a new password' : 'Change password'}</p>
      {forced && <p className="hint">You signed in with a temporary password. Choose your own to continue.</p>}
      <label htmlFor="cp-cur">Current password</label>
      <PasswordInput id="cp-cur" name="current" autoComplete="current-password" required autoFocus />
      <label htmlFor="cp-new">New password ({MIN}+ characters)</label>
      <PasswordInput id="cp-new" name="new" autoComplete="new-password" required onChange={(e) => setNext(e.target.value)} />
      <p className="hint" aria-live="polite">{Math.min(next.length, MIN)} of {MIN} characters{next.length >= MIN ? ' — long enough' : ''}</p>
      <label htmlFor="cp-new2">Confirm new password</label>
      <PasswordInput id="cp-new2" name="confirm" autoComplete="new-password" required />
      {error && <p className="err" role="alert">{error}</p>}
      <div style={{ marginTop: 20 }}>
        <button className="btn" type="submit" disabled={busy} style={{ width: forced ? '100%' : undefined }}>
          {busy ? 'Saving…' : forced ? 'Set password and continue' : 'Change password'}
        </button>{' '}
        {forced
          ? <button type="button" className="btn ghost" onClick={() => auth.logout()}>Sign out</button>
          : <button type="button" className="btn ghost" onClick={onCancel}>Cancel</button>}
      </div>
    </form>
  )
  return forced ? <div className="login">{form}</div> : form
}
